import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Tabs,
  Input,
  Space,
  Typography,
  Tag,
  Button,
  Divider,
  Row,
  Col,
  Tooltip,
  Empty,
  Badge,
  Segmented,
  List,
  Modal,
  Dropdown,
  Menu,
  Switch,
  Select,
  DatePicker,
  Radio,
  Avatar,
  Alert,
} from "antd";
import {
  Search as SearchIcon,
  Star,
  Clock,
  Calendar,
  Users,
  ShoppingCart,
  BarChart2,
  TrendingUp,
  AlertTriangle,
  FileText,
  Settings,
  ChevronRight,
  Download,
  Mail,
  Grid,
  Layers,
  PieChart,
  Activity,
  CheckCircle,
  MoreVertical,
  X,
  Zap,
  Shield,
  PauseCircle,
  PlayCircle,
} from "react-feather";

const { Title, Text } = Typography;

const LS_FAVS = "ns_reports_favorites_v1";
const LS_RECENTS = "ns_reports_recent_v1";

const safeJsonParse = (v, fallback) => {
  try {
    const x = JSON.parse(v);
    return x ?? fallback;
  } catch {
    return fallback;
  }
};

const pill = (color, label, icon = null) => (
  <Tag
    color={color}
    style={{
      borderRadius: 999,
      fontWeight: 900,
      display: "inline-flex",
      gap: 6,
      alignItems: "center",
      letterSpacing: 0.2,
    }}
  >
    {icon ? <span style={{ display: "inline-flex" }}>{icon}</span> : null}
    {label}
  </Tag>
);

// -------------------------------------
// Mock data (replace later with API/config)
// -------------------------------------
const MOCK_KPIS = [
  { key: "covers", label: "Covers today", value: "214", delta: "+8%", icon: <Users size={16} /> },
  { key: "wait", label: "Avg wait", value: "12m", delta: "-3m", icon: <Clock size={16} /> },
  { key: "noshow", label: "No-shows", value: "12", delta: "+2", icon: <AlertTriangle size={16} /> },
  { key: "direct", label: "Direct share", value: "42%", delta: "+6%", icon: <TrendingUp size={16} /> },
];

const REPORT_MODULES = [
  { key: "restaurant", title: "Restaurant Ops", icon: <ShoppingCart size={16} /> },
  { key: "booking", title: "Bookings & Distribution", icon: <Calendar size={16} /> },
  { key: "menu", title: "Digital Menu", icon: <Grid size={16} /> },
  { key: "experience", title: "Guest Experience", icon: <Activity size={16} /> },
  { key: "finance", title: "Finance & MIS", icon: <PieChart size={16} /> },
];

const REPORTS = [
  // Restaurant Ops
  {
    id: "r_waitlist_throughput",
    module: "restaurant",
    title: "Waitlist Throughput",
    description: "Seated %, dropped %, avg/max wait, peak windows, staff performance.",
    tags: ["Daily", "Operations"],
    frequency: "Daily",
    difficulty: "Core",
    icon: <Users size={16} />,
  },
  {
    id: "r_table_utilization",
    module: "restaurant",
    title: "Table Utilization & Turns",
    description: "Turns/table, occupancy %, cleaning time, overrun risk, floor drill-down.",
    tags: ["Daily", "Revenue"],
    frequency: "Daily",
    difficulty: "Core",
    icon: <Layers size={16} />,
  },
  {
    id: "r_reservations_vs_walkins",
    module: "restaurant",
    title: "Reservations vs Walk-ins",
    description: "Hourly split, outcomes (seated/no-show/cancel), conversion to covers.",
    tags: ["Daily", "Operations"],
    frequency: "Daily",
    difficulty: "Core",
    icon: <BarChart2 size={16} />,
  },
  {
    id: "r_no_show_cancellation",
    module: "restaurant",
    title: "No-show & Cancellation Analysis",
    description: "By time window, party size, channel, VIP, policy compliance, loss estimate.",
    tags: ["Weekly", "Risk"],
    frequency: "Weekly",
    difficulty: "Advanced",
    icon: <AlertTriangle size={16} />,
  },

  // Booking & Distribution
  {
    id: "b_direct_funnel",
    module: "booking",
    title: "Direct Booking Funnel",
    description: "Visits → search → selection → payment → confirmation (drop-offs).",
    tags: ["Daily", "Revenue"],
    frequency: "Daily",
    difficulty: "Advanced",
    icon: <TrendingUp size={16} />,
  },
  {
    id: "b_channel_mix",
    module: "booking",
    title: "Channel Mix & Cost of Sale",
    description: "Direct vs OTA, commission estimate, trends, rate parity signals (if CM integrated).",
    tags: ["Weekly", "Revenue"],
    frequency: "Weekly",
    difficulty: "Advanced",
    icon: <PieChart size={16} />,
  },

  // Digital Menu
  {
    id: "m_menu_engagement",
    module: "menu",
    title: "Menu Engagement",
    description: "Views by category, top clicked items, device/time trends, QR performance.",
    tags: ["Daily", "Experience"],
    frequency: "Daily",
    difficulty: "Core",
    icon: <Grid size={16} />,
  },
  {
    id: "m_item_popularity",
    module: "menu",
    title: "Item Popularity & Drop-offs",
    description: "Top items, low performers, category leakage, promo impact (if tracked).",
    tags: ["Weekly", "Revenue"],
    frequency: "Weekly",
    difficulty: "Core",
    icon: <BarChart2 size={16} />,
  },

  // Guest Experience
  {
    id: "x_sentiment_summary",
    module: "experience",
    title: "Sentiment & Feedback Summary",
    description: "Pulse 360: ratings, sentiment themes, recurring issues, action suggestions.",
    tags: ["Daily", "Experience"],
    frequency: "Daily",
    difficulty: "Core",
    icon: <Activity size={16} />,
  },
  {
    id: "x_service_recovery",
    module: "experience",
    title: "Service Recovery Tracker",
    description: "Incidents, resolution time, repeat complaints, staff coaching signals.",
    tags: ["Weekly", "Ops"],
    frequency: "Weekly",
    difficulty: "Advanced",
    icon: <CheckCircle size={16} />,
  },

  // Finance & MIS
  {
    id: "f_daily_mis_pack",
    module: "finance",
    title: "Daily MIS Pack (GM)",
    description: "Covers, revenue, no-shows, channel mix, top issues, staffing notes (export-ready).",
    tags: ["Daily", "MIS"],
    frequency: "Daily",
    difficulty: "Core",
    icon: <FileText size={16} />,
  },
  {
    id: "f_monthly_executive",
    module: "finance",
    title: "Monthly Executive Summary",
    description: "Roll-up summary: trends, peaks, risks, top opportunities (chain-ready).",
    tags: ["Monthly", "MIS"],
    frequency: "Monthly",
    difficulty: "Advanced",
    icon: <TrendingUp size={16} />,
  },
];

const MOCK_SCHEDULED = [
  {
    id: "s1",
    title: "Daily MIS Pack (GM)",
    cadence: "Every day • 9:00 AM",
    channel: "Email",
    to: "gm@hotel.com",
    lastSent: "Today 9:02 AM",
    nextRun: "Tomorrow 9:00 AM",
    status: "ok", // ok | failed | paused
  },
  {
    id: "s2",
    title: "No-show & Cancellation Analysis",
    cadence: "Every Monday • 10:00 AM",
    channel: "Email",
    to: "ops@group.com",
    lastSent: "Mon 10:01 AM",
    nextRun: "Next Mon 10:00 AM",
    status: "ok",
  },
];

// -------------------------------------
// Helpers
// -------------------------------------
const reportById = (id) => REPORTS.find((r) => r.id === id) || null;

function sortByRecents(list, recents) {
  // ✅ Fix: if not found in recents, push to bottom
  const idx = (id) => {
    const i = recents.indexOf(id);
    return i === -1 ? 9999 : i;
  };
  return [...list].sort((a, b) => idx(a.id) - idx(b.id));
}

function getPinnedCandidates({ favorites, recents }) {
  // Enterprise-friendly: pinned = favorites first, then recent
  const pinnedIds = [...favorites, ...recents].slice(0, 3);
  const pinned = pinnedIds.map(reportById).filter(Boolean);
  return pinned;
}

// -------------------------------------
// Component
// -------------------------------------
export default function ReportLandingPage({ onOpenReport }) {
  const [activeModule, setActiveModule] = useState("restaurant");
  const [q, setQ] = useState("");
  const [viewMode, setViewMode] = useState("browse"); // browse | favorites | recent
  const [timeScope, setTimeScope] = useState("7d"); // today|7d|30d|custom
  const [favorites, setFavorites] = useState(() => safeJsonParse(localStorage.getItem(LS_FAVS), []));
  const [recents, setRecents] = useState(() => safeJsonParse(localStorage.getItem(LS_RECENTS), []));
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null);

  // scheduling mock fields
  const [schedEnabled, setSchedEnabled] = useState(true);
  const [schedCadence, setSchedCadence] = useState("daily");
  const [schedTime, setSchedTime] = useState("09:00");
  const [schedChannel, setSchedChannel] = useState("email");
  const [schedFormat, setSchedFormat] = useState("pdf");
  const [schedRecipients, setSchedRecipients] = useState(["gm@hotel.com"]);
  const [schedScope, setSchedScope] = useState("property");
  const [schedIncludeSummary, setSchedIncludeSummary] = useState(true);

  useEffect(() => {
    localStorage.setItem(LS_FAVS, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(LS_RECENTS, JSON.stringify(recents));
  }, [recents]);

  const isFav = useCallback((id) => favorites.includes(id), [favorites]);

  const toggleFav = useCallback((id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev].slice(0, 50)));
  }, []);

  const pushRecent = useCallback((id) => {
    setRecents((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, 20));
  }, []);

  const normalizedQuery = useMemo(() => String(q || "").trim().toLowerCase(), [q]);

  const statsByModule = useMemo(() => {
    const by = {};
    REPORT_MODULES.forEach((m) => {
      by[m.key] = REPORTS.filter((r) => r.module === m.key).length;
    });
    return by;
  }, []);

  const pinnedReports = useMemo(() => getPinnedCandidates({ favorites, recents }), [favorites, recents]);

  const moduleReports = useMemo(() => {
    const base = REPORTS.filter((r) => r.module === activeModule);

    const filtered = base.filter((r) => {
      if (!normalizedQuery) return true;
      const hay = `${r.title} ${r.description} ${(r.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });

    if (viewMode === "favorites") return filtered.filter((r) => favorites.includes(r.id));
    if (viewMode === "recent") return sortByRecents(filtered, recents);

    return filtered;
  }, [activeModule, normalizedQuery, viewMode, favorites, recents]);

  const openReport = useCallback(
    (report) => {
      pushRecent(report.id);
      if (typeof onOpenReport === "function") onOpenReport(report);
      else {
        Modal.info({
          title: report.title,
          content: (
            <div style={{ color: "#374151" }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Mock open</div>
              <div style={{ marginBottom: 10 }}>{report.description}</div>
              <div>Later: route to real report page + filters + export + schedule.</div>
            </div>
          ),
          okText: "Close",
        });
      }
    },
    [onOpenReport, pushRecent]
  );

  const openSchedule = useCallback((report) => {
    setScheduleFor(report);
    setScheduleOpen(true);

    // reset mock fields for nicer UX
    setSchedEnabled(true);
    setSchedCadence("daily");
    setSchedTime("09:00");
    setSchedChannel("email");
    setSchedFormat("pdf");
    setSchedRecipients(["gm@hotel.com"]);
    setSchedScope("property");
    setSchedIncludeSummary(true);
  }, []);

  const kpiTiles = useMemo(() => {
    return MOCK_KPIS.map((k) => (
      <Col key={k.key} xs={12} sm={12} md={12} lg={6} xl={6}>
        <Card
          className="rp-kpi"
          style={{
            borderRadius: 16,
            border: "1px solid #eef2f7",
            boxShadow: "0 10px 24px rgba(17,24,39,.04)",
          }}
          bodyStyle={{ padding: 14 }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 800 }}>{k.label}</div>
              <div style={{ marginTop: 6, fontSize: 22, fontWeight: 950, color: "#111827" }}>{k.value}</div>
              <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                <span style={{ fontWeight: 950, color: "#111827" }}>{k.delta}</span> vs last week
              </div>
            </div>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 14,
                background: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
              }}
            >
              {k.icon}
            </div>
          </div>
        </Card>
      </Col>
    ));
  }, []);

  const InsightStrip = () => (
    <div className="rp-insight">
      <div className="rp-insightLeft">
        <div className="rp-insightTitle">
          <Zap size={16} /> Operational insight
        </div>
        <div className="rp-insightText">
          <b>Risk:</b> Friday dinner slots trending overbooked • <b>Recommendation:</b> push early slots with incentive •{" "}
          <b>Alert:</b> No-shows up vs last week
        </div>
      </div>
      <Space wrap size={8} className="rp-insightActions">
        {pill("default", "Export-ready", <Download size={14} />)}
        {pill("blue", "Live", <Activity size={14} />)}
        <Button className="rp-btnSoft" icon={<Mail size={16} />}>
          Schedule
        </Button>
      </Space>
    </div>
  );

  const FilterRow = () => (
    <div className="rp-filters">
      <Space wrap size={8}>
        <div className="rp-filterLabel">Time</div>
        <Segmented
          value={timeScope}
          onChange={setTimeScope}
          options={[
            { label: "Today", value: "today" },
            { label: "7D", value: "7d" },
            { label: "30D", value: "30d" },
            { label: "Custom", value: "custom" },
          ]}
        />
        {timeScope === "custom" ? (
          <Space wrap size={8}>
            <DatePicker.RangePicker style={{ borderRadius: 12 }} />
          </Space>
        ) : null}
      </Space>

      <Space wrap size={8}>
        <div className="rp-filterLabel">Tags</div>
        <Select
          allowClear
          placeholder="All"
          style={{ minWidth: 180 }}
          options={[
            { value: "Operations", label: "Operations" },
            { value: "Revenue", label: "Revenue" },
            { value: "Experience", label: "Experience" },
            { value: "MIS", label: "MIS" },
            { value: "Risk", label: "Risk" },
          ]}
        />
        <Tooltip title="Permissions-ready (mock)">
          <Button className="rp-btnSoft" icon={<Shield size={16} />}>
            Scope
          </Button>
        </Tooltip>
      </Space>
    </div>
  );

  const TabLabel = ({ icon, title, count }) => (
    <span className="rp-tab">
      <span className="rp-tabIcon">{icon}</span>
      <span className="rp-tabTitle">{title}</span>
      <Badge count={count} overflowCount={999} className="rp-tabBadge" />
    </span>
  );

  TabLabel.propTypes = {
    icon: PropTypes.node,
    title: PropTypes.string,
    count: PropTypes.number,
  };

  const CardMenu = (report) => ({
    items: [
      {
        key: "open",
        label: "Open",
        icon: <ChevronRight size={14} />,
        onClick: () => openReport(report),
      },
      {
        key: "schedule",
        label: "Schedule",
        icon: <Mail size={14} />,
        onClick: () => openSchedule(report),
      },
      {
        key: "export",
        label: "Export",
        icon: <Download size={14} />,
      },
      { type: "divider" },
      {
        key: "fav",
        label: isFav(report.id) ? "Remove from favorites" : "Add to favorites",
        icon: <Star size={14} />,
        onClick: () => toggleFav(report.id),
      },
    ],
  });

  const ReportCard = ({ r }) => {
    const fav = isFav(r.id);

    return (
      <Card
        hoverable
        className="rp-reportCard"
        style={{
          borderRadius: 16,
          border: "1px solid #eef2f7",
          boxShadow: "0 10px 22px rgba(17,24,39,.04)",
          height: 192,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        bodyStyle={{ padding: 14, flex: 1, display: "flex", flexDirection: "column" }}
        onClick={() => openReport(r)}
      >
        <div className="rp-cardAccent" />

        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="rp-iconBox">{r.icon}</div>
              <div style={{ minWidth: 0 }}>
                <div className="rp-cardTitle">{r.title}</div>
                <div className="rp-cardMeta">
                  {r.frequency} • {r.difficulty}
                </div>
              </div>
            </div>
          </div>

          <Space size={4}>
            <Tooltip title={fav ? "Favorited" : "Add to favorites"}>
              <Button
                type="text"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFav(r.id);
                }}
                icon={<Star size={18} />}
                style={{ borderRadius: 12, color: fav ? "#f59e0b" : "#6b7280" }}
              />
            </Tooltip>

            <Dropdown menu={CardMenu(r)} trigger={["click"]}>
              <Button
                type="text"
                onClick={(e) => e.stopPropagation()}
                icon={<MoreVertical size={18} />}
                style={{ borderRadius: 12, color: "#6b7280" }}
              />
            </Dropdown>
          </Space>
        </div>

        <div className="rp-cardDesc">{r.description}</div>

        <div className="rp-cardTags">
          {(r.tags || []).slice(0, 3).map((t) => (
            <Tag key={t} className="rp-tagSoft">
              {t}
            </Tag>
          ))}
        </div>

        <div className="rp-cardFooter">
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              openReport(r);
            }}
            icon={<ChevronRight size={16} />}
            className="rp-btnPrimary"
          >
            Open
          </Button>

          <Button
            className="rp-btnSoft"
            onClick={(e) => {
              e.stopPropagation();
              openSchedule(r);
            }}
            icon={<Mail size={16} />}
          >
            Schedule
          </Button>
        </div>
      </Card>
    );
  };

  ReportCard.propTypes = { r: PropTypes.object.isRequired };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header */}
        <div className="rp-header">
          <div style={{ minWidth: 0 }}>
            <Title level={3} style={{ margin: 0, letterSpacing: -0.2 }}>
              Reports
            </Title>
            <Text type="secondary">
              Enterprise-ready analytics for reservations, waitlist, tables, direct bookings, digital menu, and guest experience.
            </Text>
          </div>

          <Space wrap size={10} className="rp-headerRight">
            <Input
              allowClear
              value={q}
              onChange={(e) => setQ(e.target.value)}
              prefix={<SearchIcon size={16} />}
              placeholder="Search reports… (waitlist, no-show, conversion, menu)"
              className="rp-search"
            />

            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                {
                  label: (
                    <span className="rp-segLabel">
                      <Grid size={14} /> Browse
                    </span>
                  ),
                  value: "browse",
                },
                {
                  label: (
                    <span className="rp-segLabel">
                      <Star size={14} /> Favorites
                    </span>
                  ),
                  value: "favorites",
                },
                {
                  label: (
                    <span className="rp-segLabel">
                      <Clock size={14} /> Recent
                    </span>
                  ),
                  value: "recent",
                },
              ]}
            />
          </Space>
        </div>

        <Divider style={{ margin: "14px 0" }} />

        {/* Today Snapshot */}
        <Card className="rp-block" bodyStyle={{ padding: 14 }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="rp-heroIcon">
                  <BarChart2 size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 950, color: "#111827" }}>Today snapshot</div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>Fast answers for ops team & managers</div>
                </div>
              </div>
            </div>

            <Space wrap>
              {pill("blue", "Live", <Activity size={14} />)}
              {pill("default", "Export-ready", <Download size={14} />)}
              <Button className="rp-btnSoft" icon={<Mail size={16} />}>
                Schedule
              </Button>
            </Space>
          </Space>

          <Divider style={{ margin: "12px 0" }} />

          <Row gutter={[12, 12]}>{kpiTiles}</Row>

          <Divider style={{ margin: "12px 0" }} />

          <InsightStrip />
        </Card>

        {/* Filters */}
        <FilterRow />

        {/* Pinned */}
        {pinnedReports.length ? (
          <Card className="rp-block" bodyStyle={{ padding: 14 }} style={{ marginTop: 14 }}>
            <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
              <div>
                <div style={{ fontWeight: 950, color: "#111827" }}>Pinned</div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>Quick access to your most used reports</div>
              </div>
              {pill("default", `${pinnedReports.length} items`, <Star size={14} />)}
            </Space>

            <Divider style={{ margin: "10px 0" }} />

            <Row gutter={[12, 12]}>
              {pinnedReports.map((r) => (
                <Col key={r.id} xs={24} md={12} lg={8}>
                  <ReportCard r={r} />
                </Col>
              ))}
            </Row>
          </Card>
        ) : null}

        {/* Main: Tabs + Scheduled */}
        <Row gutter={[14, 14]} align="top" style={{ marginTop: 14 }}>
          {/* Tabs / Catalog */}
          <Col xs={24} md={16} lg={17}>
            <Card className="rp-block" bodyStyle={{ padding: 12 }}>
              <Tabs
                activeKey={activeModule}
                onChange={setActiveModule}
                size="large"
                tabBarGutter={22}
                tabBarStyle={{ marginBottom: 6 }}
                // ✅ avoids truncated labels on mid widths
                moreIcon={<MoreVertical size={16} />}
              >
                {REPORT_MODULES.map((m) => (
                  <Tabs.TabPane
                    key={m.key}
                    tab={<TabLabel icon={m.icon} title={m.title} count={statsByModule[m.key]} />}
                  >
                    <Divider style={{ margin: "10px 0" }} />

                    {moduleReports.length === 0 ? (
                      <div style={{ padding: "30px 0" }}>
                        <Empty
                          description={
                            <div>
                              <div style={{ fontWeight: 950, color: "#111827" }}>No reports found</div>
                              <div style={{ color: "#6b7280" }}>Try a different keyword or switch module.</div>
                            </div>
                          }
                        />
                      </div>
                    ) : (
                      <Row gutter={[12, 12]}>
                        {moduleReports.map((r) => (
                          <Col key={r.id} xs={24} sm={12} md={12} lg={8} xl={8}>
                            <ReportCard r={r} />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Tabs.TabPane>
                ))}
              </Tabs>
            </Card>
          </Col>

          {/* Scheduled / Quick Access */}
          <Col xs={24} md={8} lg={7}>
            <Card className="rp-block" bodyStyle={{ padding: 12 }}>
              <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
                <div>
                  <div style={{ fontWeight: 950, color: "#111827" }}>Scheduled reports</div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>Auto email packs for managers</div>
                </div>
                <Button className="rp-btnSoft" icon={<Settings size={16} />}>
                  Manage
                </Button>
              </Space>

              <Divider style={{ margin: "10px 0" }} />

              <List
                dataSource={MOCK_SCHEDULED}
                renderItem={(it) => {
                  const statusBadge =
                    it.status === "paused"
                      ? pill("default", "Paused", <PauseCircle size={14} />)
                      : it.status === "failed"
                      ? pill("red", "Failed", <AlertTriangle size={14} />)
                      : pill("green", "OK", <CheckCircle size={14} />);

                  return (
                    <List.Item style={{ paddingInline: 0 }}>
                      <div style={{ width: "100%" }}>
                        <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 950, color: "#111827" }}>{it.title}</div>
                            <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>{it.cadence}</div>
                          </div>
                          {statusBadge}
                        </Space>

                        <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12, lineHeight: 1.4 }}>
                          <div>
                            {it.channel} • <span style={{ color: "#111827", fontWeight: 900 }}>{it.to}</span>
                          </div>
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                            <span>
                              <b style={{ color: "#111827" }}>Last:</b> {it.lastSent}
                            </span>
                            <span>
                              <b style={{ color: "#111827" }}>Next:</b> {it.nextRun}
                            </span>
                          </div>
                        </div>

                        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                          <Button className="rp-btnSoft" icon={<Mail size={16} />} onClick={() => openSchedule(reportById("f_daily_mis_pack") || null)}>
                            Edit
                          </Button>
                          <Button className="rp-btnSoft" icon={it.status === "paused" ? <PlayCircle size={16} /> : <PauseCircle size={16} />}>
                            {it.status === "paused" ? "Resume" : "Pause"}
                          </Button>
                        </div>

                        <Divider style={{ margin: "12px 0" }} />
                      </div>
                    </List.Item>
                  );
                }}
              />

              <Alert
                type="info"
                showIcon
                icon={<Shield size={16} />}
                message="Enterprise tip"
                description="For chains, enable “Group View” reports with drill-down and role permissions. Same UX—just wider scope."
                style={{ borderRadius: 16 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Schedule modal (richer mock) */}
        <Modal
          open={scheduleOpen}
          onCancel={() => setScheduleOpen(false)}
          title={
            <span style={{ display: "inline-flex", gap: 10, alignItems: "center", fontWeight: 950 }}>
              <Mail size={18} /> Schedule report
            </span>
          }
          footer={
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <Space>
                <Button
                  icon={<X size={16} />}
                  onClick={() => setScheduleOpen(false)}
                  className="rp-btnSoft"
                >
                  Close
                </Button>
                <Tooltip title="Mock preview">
                  <Button icon={<FileText size={16} />} className="rp-btnSoft">
                    Preview
                  </Button>
                </Tooltip>
              </Space>

              <Space>
                <Button
                  icon={<Download size={16} />}
                  className="rp-btnSoft"
                >
                  Export now
                </Button>
                <Button
                  type="primary"
                  className="rp-btnPrimary"
                  icon={<CheckCircle size={16} />}
                  onClick={() => {
                    setScheduleOpen(false);
                    Modal.success({
                      title: "Scheduled (mock)",
                      content: "Later: save schedule to DB + connect PM2 worker:email for delivery.",
                      okText: "Done",
                    });
                  }}
                >
                  Save schedule
                </Button>
              </Space>
            </div>
          }
          width={720}
        >
          <div style={{ color: "#374151" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 950, color: "#111827" }}>{scheduleFor?.title || "Report"}</div>
                <div style={{ color: "#6b7280", marginTop: 2 }}>
                  Choose cadence, recipients, delivery format, and access scope.
                </div>
              </div>
              <Space>
                {pill("default", "Export-ready", <Download size={14} />)}
                {pill("blue", "Live", <Activity size={14} />)}
              </Space>
            </div>

            <Divider />

            <Card className="rp-subCard" bodyStyle={{ padding: 12 }}>
              <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
                <Space>
                  <div className="rp-miniIcon">
                    <Mail size={16} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 950, color: "#111827" }}>Delivery</div>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>Enable/disable and configure recipients</div>
                  </div>
                </Space>

                <Space>
                  <Text style={{ color: "#6b7280" }}>Enabled</Text>
                  <Switch checked={schedEnabled} onChange={setSchedEnabled} />
                </Space>
              </Space>

              <Divider style={{ margin: "10px 0" }} />

              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <div className="rp-fieldLabel">Cadence</div>
                  <Radio.Group value={schedCadence} onChange={(e) => setSchedCadence(e.target.value)} style={{ width: "100%" }}>
                    <Space wrap>
                      <Radio.Button value="daily">Daily</Radio.Button>
                      <Radio.Button value="weekly">Weekly</Radio.Button>
                      <Radio.Button value="monthly">Monthly</Radio.Button>
                      <Radio.Button value="custom">Custom</Radio.Button>
                    </Space>
                  </Radio.Group>
                </Col>

                <Col xs={24} md={12}>
                  <div className="rp-fieldLabel">Time</div>
                  <Select
                    value={schedTime}
                    onChange={setSchedTime}
                    style={{ width: "100%" }}
                    options={[
                      { value: "08:00", label: "08:00 AM" },
                      { value: "09:00", label: "09:00 AM" },
                      { value: "10:00", label: "10:00 AM" },
                      { value: "18:00", label: "06:00 PM" },
                    ]}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <div className="rp-fieldLabel">Channel</div>
                  <Select
                    value={schedChannel}
                    onChange={setSchedChannel}
                    style={{ width: "100%" }}
                    options={[
                      { value: "email", label: "Email" },
                      { value: "whatsapp", label: "WhatsApp (later)" },
                      { value: "link", label: "Download link" },
                    ]}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <div className="rp-fieldLabel">Format</div>
                  <Select
                    value={schedFormat}
                    onChange={setSchedFormat}
                    style={{ width: "100%" }}
                    options={[
                      { value: "pdf", label: "PDF" },
                      { value: "csv", label: "CSV" },
                      { value: "xlsx", label: "XLSX" },
                    ]}
                  />
                </Col>

                <Col xs={24}>
                  <div className="rp-fieldLabel">Recipients</div>
                  <Select
                    mode="tags"
                    value={schedRecipients}
                    onChange={setSchedRecipients}
                    style={{ width: "100%" }}
                    tokenSeparators={[",", " "]}
                    placeholder="Add emails…"
                  />
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                    Tip: use role groups later (GM, Ops, Finance) instead of emails.
                  </div>
                </Col>
              </Row>
            </Card>

            <Divider />

            <Card className="rp-subCard" bodyStyle={{ padding: 12 }}>
              <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
                <Space>
                  <div className="rp-miniIcon">
                    <Shield size={16} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 950, color: "#111827" }}>Scope & permissions</div>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>Enterprise controls (mock)</div>
                  </div>
                </Space>
                {pill("default", "Role-based", <Shield size={14} />)}
              </Space>

              <Divider style={{ margin: "10px 0" }} />

              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <div className="rp-fieldLabel">Scope</div>
                  <Select
                    value={schedScope}
                    onChange={setSchedScope}
                    style={{ width: "100%" }}
                    options={[
                      { value: "property", label: "Property only" },
                      { value: "group", label: "Group-wide (chain)" },
                      { value: "outlet", label: "Outlet / floor" },
                    ]}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <div className="rp-fieldLabel">Include executive summary</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #eef2f7", borderRadius: 12, padding: "8px 10px" }}>
                    <Text style={{ color: "#111827", fontWeight: 900 }}>Summary insights</Text>
                    <Switch checked={schedIncludeSummary} onChange={setSchedIncludeSummary} />
                  </div>
                </Col>
              </Row>
            </Card>

            <Divider />

            <div className="rp-previewBox">
              <div style={{ fontWeight: 950, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
                <FileText size={16} /> Preview (mock)
              </div>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                This is how the recipient will see the pack.
              </div>

              <div className="rp-previewEmail">
                <div className="rp-previewEmailTop">
                  <Avatar size={34} style={{ background: "#111827" }}>
                    N
                  </Avatar>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 950, color: "#111827" }}>NetShine ONE Reports</div>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>Daily pack • {schedFormat.toUpperCase()} • {schedTime}</div>
                  </div>
                </div>

                <div style={{ marginTop: 10, color: "#374151", fontSize: 12, lineHeight: 1.5 }}>
                  <b>{scheduleFor?.title || "Report"}</b> is scheduled for <b>{schedCadence}</b> delivery to{" "}
                  <b>{(schedRecipients || []).join(", ") || "—"}</b>. Includes key KPIs, exceptions, and insights.
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, background: "#fbfcfe", border: "1px dashed #e5e7eb", borderRadius: 16, padding: 12 }}>
              <div style={{ fontWeight: 950, color: "#111827" }}>Next step</div>
              <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                We’ll store schedules in DB and wire delivery into your <b>PM2 worker:email</b>.
              </div>
            </div>
          </div>
        </Modal>

        {/* Local CSS polish + iPad friendliness */}
        <style>{`
          .rp-header{
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap:12px;
            flex-wrap:wrap;
          }

          .rp-headerRight{
            justify-content:flex-end;
          }

          .rp-search{
            width:360px;
            border-radius:12px;
            height:38px;
          }
          .rp-search .ant-input{
            height:38px;
          }

          .rp-block{
            border-radius:18px;
            border:1px solid #eef2f7;
            box-shadow:0 12px 30px rgba(17,24,39,.05);
          }

          .rp-heroIcon{
            width:38px;
            height:38px;
            border-radius:14px;
            background:#111827;
            color:#fff;
            display:flex;
            align-items:center;
            justify-content:center;
          }

          .rp-kpi{
            background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
          }

          .rp-filters{
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:12px;
            flex-wrap:wrap;
            padding:12px 14px;
            margin-top:14px;
            background:#ffffff;
            border:1px solid #eef2f7;
            border-radius:18px;
            box-shadow:0 10px 24px rgba(17,24,39,.04);
          }

          .rp-filterLabel{
            font-size:12px;
            color:#6b7280;
            font-weight:900;
            margin-right:6px;
          }

          .rp-segLabel{
            display:inline-flex;
            align-items:center;
            gap:8px;
            font-weight:900;
          }

          .rp-tab{
            display:inline-flex;
            align-items:center;
            gap:10px;
          }
          .rp-tabTitle{
            font-weight:900;
          }
          .rp-tabBadge .ant-badge-count{
            background:#111827 !important;
            box-shadow:none !important;
          }

          .rp-btnSoft{
            border-radius:12px !important;
            height:36px;
          }
          .rp-btnPrimary{
            border-radius:12px !important;
            height:36px;
            flex:1;
          }

          .rp-reportCard{
            transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          }
          .rp-reportCard:hover{
            transform: translateY(-2px);
            box-shadow: 0 18px 40px rgba(17,24,39,.10) !important;
            border-color:#e6ecf5 !important;
          }
          .rp-cardAccent{
            height:3px;
            width:100%;
            background: linear-gradient(90deg, rgba(17,24,39,.2), rgba(17,24,39,0));
            margin:-14px -14px 12px -14px;
          }
          .rp-iconBox{
            width:38px;
            height:38px;
            border-radius:14px;
            background:#f3f4f6;
            display:flex;
            align-items:center;
            justify-content:center;
            flex:0 0 auto;
          }
          .rp-cardTitle{
            font-weight:950;
            color:#111827;
            line-height:1.15;
            white-space:nowrap;
            overflow:hidden;
            text-overflow:ellipsis;
          }
          .rp-cardMeta{
            color:#6b7280;
            font-size:12px;
          }
          .rp-cardDesc{
            margin-top:10px;
            color:#374151;
            font-size:12px;
            line-height:1.35;
            flex:1;
          }
          .rp-cardTags{
            margin-top:10px;
            display:flex;
            gap:8px;
            flex-wrap:wrap;
          }
          .rp-tagSoft{
            border-radius:999px !important;
            font-weight:900 !important;
          }
          .rp-cardFooter{
            margin-top:12px;
            display:flex;
            gap:8px;
          }

          .rp-insight{
            background:#fbfcfe;
            border:1px solid #eef2f7;
            border-radius:16px;
            padding:10px 12px;
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap:10px;
            flex-wrap:wrap;
          }
          .rp-insightTitle{
            font-weight:950;
            color:#111827;
            display:flex;
            align-items:center;
            gap:8px;
          }
          .rp-insightText{
            margin-top:4px;
            color:#6b7280;
            font-size:12px;
            line-height:1.4;
          }

          .rp-subCard{
            border-radius:16px !important;
            border:1px solid #eef2f7 !important;
          }
          .rp-miniIcon{
            width:34px;
            height:34px;
            border-radius:14px;
            background:#f3f4f6;
            display:flex;
            align-items:center;
            justify-content:center;
          }
          .rp-fieldLabel{
            font-size:12px;
            color:#6b7280;
            font-weight:950;
            margin-bottom:6px;
          }

          .rp-previewBox{
            background:#fbfcfe;
            border:1px solid #eef2f7;
            border-radius:16px;
            padding:12px;
          }
          .rp-previewEmail{
            margin-top:10px;
            background:#fff;
            border:1px solid #eef2f7;
            border-radius:14px;
            padding:12px;
            box-shadow: 0 10px 24px rgba(17,24,39,.04);
          }
          .rp-previewEmailTop{
            display:flex;
            gap:10px;
            align-items:center;
          }

          /* -------------------------
             iPad Max friendliness
             ------------------------- */

          @media (max-width: 1024px){
            .rp-search{ width: 100% !important; }
            /* KPI: 2 per row already ensured by md=12 */
          }

          /* phones/small: 1 column cards */
          @media (max-width: 576px){
            .rp-cardFooter{ flex-direction:column; }
            .rp-btnPrimary{ width:100%; }
          }
        `}</style>
      </div>
    </div>
  );
}

ReportLandingPage.propTypes = {
  onOpenReport: PropTypes.func, // optional: (report) => navigate(`/reports/${report.id}`)
};
